import { motion } from 'framer-motion';
import {
  Calendar,
  Car,
  GraduationCap,
  Heart,
  Home,
  IndianRupee,
  Info,
  Plane,
  Target,
} from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { ToolLayout } from '@layouts/ToolLayout';

import { LocalizedCurrency } from '@ui/LocalizedNumber';
import NumberInputWithSlider from '@ui/NumberInputWithSlider';
import { WhatsAppCTA } from '@ui/WhatsAppCTA';

import { COLORS } from '@constants/colors';
import { CONTACT_INFO } from '@constants/contact';

import {
  formatCurrency,
  formatIndianNumberShort,
} from '@utils/numberFormatter';

// Goal types with predefined values (moved outside component to prevent re-creation)
const goalTypes = [
  {
    value: 'home',
    label: 'Home purchase',
    description: `${formatIndianNumberShort(5000000, false)} typical`,
    icon: Home,
    defaultAmount: 5000000,
    color: COLORS.accent,
  },
  {
    value: 'education',
    label: 'Child education',
    description: `${formatIndianNumberShort(2000000, false)} typical`,
    icon: GraduationCap,
    defaultAmount: 2000000,
    color: COLORS.sage,
  },
  {
    value: 'car',
    label: 'Car purchase',
    description: `${formatIndianNumberShort(1500000, false)} typical`,
    icon: Car,
    defaultAmount: 1500000,
    color: COLORS.gold,
  },
  {
    value: 'vacation',
    label: 'Dream Vacation',
    description: `${formatIndianNumberShort(500000, false)} typical`,
    icon: Plane,
    defaultAmount: 500000,
    color: COLORS.secondary,
  },
  {
    value: 'wedding',
    label: 'Wedding',
    description: `${formatIndianNumberShort(1000000, false)} typical`,
    icon: Heart,
    defaultAmount: 1000000,
    color: '#E91E63',
  },
  {
    value: 'custom',
    label: 'Custom goal',
    description: 'Set your amount',
    icon: Target,
    defaultAmount: 1000000,
    color: COLORS.charcoal,
  },
];

export const GoalPlanner = () => {
  // Questionnaire State
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [selectedGoalType, setSelectedGoalType] = useState<string>('');
  const [goalAmount, setGoalAmount] = useState<number | null>(null);
  const [timeToGoal, setTimeToGoal] = useState<number | null>(10);
  const [inflationRate, setInflationRate] = useState<number | null>(6);
  const [expectedReturn, setExpectedReturn] = useState<number | null>(12);

  // Ref to track previous goal type to prevent unnecessary resets
  const prevGoalTypeRef = useRef<string>('');

  // Get selected goal object (memoized to prevent unnecessary re-renders)
  const selectedGoal = React.useMemo(() => {
    return (
      goalTypes.find(goal => goal.value === selectedGoalType) || goalTypes[0]
    );
  }, [selectedGoalType]);

  // Generate smart WhatsApp message based on available information
  const generateWhatsAppMessage = () => {
    const hasGoal = selectedGoalType && selectedGoalType !== '';
    const hasAmount = goalAmount && goalAmount > 0;
    const hasTimeframe = timeToGoal && timeToGoal > 0;
    const hasCalculation = results.monthlyInvestment > 0;

    if (!hasGoal) {
      return `Hi ${CONTACT_INFO.contact_person.short_name}, I'm interested in financial goal planning and would like to get personalized investment advice. Can you help me get started?`;
    }

    let message = `Hi ${CONTACT_INFO.contact_person.short_name}! I used your Goal planner for ${selectedGoal.label}`;

    if (hasAmount && hasTimeframe && hasCalculation) {
      message += ` with these details:

Goal: ${selectedGoal.label}
Target amount: ${formatCurrency(goalAmount || 0)} (today's value)
Time horizon: ${timeToGoal || 0} years
Expected return: ${expectedReturn || 0}% p.a.

CALCULATION RESULTS:
• Inflation-adjusted goal: ${formatCurrency(results.inflationAdjustedGoal)}
• Required monthly SIP: ${formatCurrency(results.monthlyInvestment)}
• Total investment: ${formatCurrency(results.totalInvestment)}
• Expected returns: ${formatCurrency(results.totalReturns)}

I need personalized advice to achieve this goal. Can you help me choose the right investment strategy and mutual funds?`;
    } else if (hasAmount && hasTimeframe) {
      message += `. I need ${formatCurrency(goalAmount || 0)} in ${timeToGoal} years. Please help me calculate the investment required and choose the right strategy.`;
    } else if (hasAmount) {
      message += ` with a target of ${formatCurrency(goalAmount || 0)}. I need help determining the timeline and investment strategy. Can you assist?`;
    } else if (hasTimeframe) {
      message += ` and want to achieve it in ${timeToGoal} years. I need help determining the target amount and investment strategy. Can you guide me?`;
    } else {
      message +=
        '. I need help determining the target amount, timeline, and investment strategy. Can you provide personalized advice?';
    }

    return message;
  };

  // Check if current step is completed
  const isStepCompleted = (step: number) => {
    switch (step) {
      case 1:
        return selectedGoalType !== '';
      case 2:
        return goalAmount !== null && goalAmount > 0;
      case 3:
        return timeToGoal !== null && timeToGoal > 0;
      case 4:
        return inflationRate !== null && inflationRate > 0;
      case 5:
        return expectedReturn !== null && expectedReturn > 0;
      default:
        return false;
    }
  };

  // Navigate to next step
  const nextStep = () => {
    if (currentStep < 6 && isStepCompleted(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  // Navigate to previous step
  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };
  const [results, setResults] = useState({
    inflationAdjustedGoal: 0,
    monthlyInvestment: 0,
    totalInvestment: 0,
    totalReturns: 0,
  });

  // Calculate goal planning
  const calculateGoal = useCallback(() => {
    if (!goalAmount || !timeToGoal || !expectedReturn || !inflationRate) {
      setResults({
        inflationAdjustedGoal: 0,
        monthlyInvestment: 0,
        totalInvestment: 0,
        totalReturns: 0,
      });
      return;
    }

    // Adjust goal amount for inflation
    const inflationAdjustedGoal =
      goalAmount * Math.pow(1 + inflationRate / 100, timeToGoal);

    // Calculate monthly SIP required
    const monthlyRate = expectedReturn / 100 / 12;
    const totalMonths = timeToGoal * 12;

    // SIP formula rearranged to find PMT: PMT = FV / [{(1 + i)^n - 1} / i]
    const monthlyInvestment =
      inflationAdjustedGoal /
      (((Math.pow(1 + monthlyRate, totalMonths) - 1) / monthlyRate) *
        (1 + monthlyRate));

    const totalInvestment = monthlyInvestment * totalMonths;
    const totalReturns = inflationAdjustedGoal - totalInvestment;

    setResults({
      inflationAdjustedGoal,
      monthlyInvestment,
      totalInvestment,
      totalReturns,
    });
  }, [goalAmount, timeToGoal, expectedReturn, inflationRate]);

  // Recalculate when inputs change
  useEffect(() => {
    calculateGoal();
  }, [calculateGoal]);

  // Update goal amount when goal type changes (only if not custom and goal type actually changed)
  useEffect(() => {
    if (
      selectedGoalType &&
      selectedGoalType !== 'custom' &&
      selectedGoalType !== prevGoalTypeRef.current
    ) {
      setGoalAmount(selectedGoal.defaultAmount);
      prevGoalTypeRef.current = selectedGoalType;
    }
  }, [selectedGoalType, selectedGoal.defaultAmount]);

  return (
    <ToolLayout
      title={`Goal planner | ${CONTACT_INFO.company.name}`}
      heroTitle="Goal planner"
      heroStyle="gradient"
      showBreadcrumb
      backgroundColor={COLORS.mist}
      description="Free goal planner to calculate how much you need to invest monthly to achieve your financial goals like home, education, car, and more."
      keywords="goal planner, financial goals, investment planning, SIP for goals, home purchase planning, education planning"
      canonicalUrl="https://niveshright.com/tools/goal-planner"
      heroIcon={<Target size={32} style={{ color: COLORS.gold }} />}
      cta={
        <WhatsAppCTA
          title={
            selectedGoalType
              ? `Ready to start planning your ${selectedGoal.label}?`
              : 'Ready to start financial planning?'
          }
          description="Get personalized investment advice to achieve your financial goals faster."
          buttonText="Get goal-based investment plan"
          message={generateWhatsAppMessage()}
          phoneNumber={CONTACT_INFO.phone.whatsapp}
          backgroundColor={COLORS.pearl}
          buttonColor={selectedGoal.color}
        />
      }
    >
      {/* Progress Indicator */}
      <div className="mb-6 sm:mb-8">
        <div className="flex items-center justify-center px-4">
          {[1, 2, 3, 4, 5, 6].map(step => (
            <div key={step} className="flex items-center">
              <div
                className={`w-6 h-6 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold transition-all duration-300 ${
                  step < currentStep || (step === 6 && currentStep === 6)
                    ? 'bg-green-500 text-white'
                    : step === currentStep
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-500'
                }`}
              >
                {step < currentStep || (step === 6 && currentStep === 6)
                  ? '✓'
                  : step}
              </div>
              {step < 6 && (
                <div
                  className={`w-4 sm:w-12 h-0.5 sm:h-1 mx-1 sm:mx-2 transition-all duration-300 ${
                    step < currentStep || (currentStep === 6 && step === 5)
                      ? 'bg-green-500'
                      : 'bg-gray-200'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
        <div className="text-center mt-3 sm:mt-4">
          <p className="text-xs sm:text-sm text-gray-600">
            Step {currentStep} of 6:{' '}
            {currentStep === 1
              ? 'Choose your goal'
              : currentStep === 2
                ? 'Set goal amount'
                : currentStep === 3
                  ? 'Timeline planning'
                  : currentStep === 4
                    ? 'Inflation consideration'
                    : currentStep === 5
                      ? 'Expected returns'
                      : 'Your investment plan'}
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto">
        {/* Step 1: Goal Selection */}
        {currentStep === 1 && (
          <motion.div
            className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-center mb-8">
              <h2
                className="text-3xl font-bold mb-4"
                style={{ color: COLORS.primary }}
              >
                What's your financial goal?
              </h2>
              <p className="text-lg text-gray-600">
                Choose the goal you want to plan for, or create a custom one
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6 mb-6 sm:mb-8">
              {goalTypes.map(goal => {
                const IconComponent = goal.icon;
                return (
                  <motion.button
                    key={goal.value}
                    onClick={() => setSelectedGoalType(goal.value)}
                    className={`p-3 sm:p-6 rounded-xl sm:rounded-2xl border-2 transition-all duration-300 text-left ${
                      selectedGoalType === goal.value
                        ? 'border-current shadow-lg scale-105'
                        : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                    }`}
                    style={{
                      borderColor:
                        selectedGoalType === goal.value
                          ? goal.color
                          : undefined,
                      backgroundColor:
                        selectedGoalType === goal.value
                          ? `${goal.color}10`
                          : 'white',
                    }}
                    whileHover={{
                      scale: selectedGoalType === goal.value ? 1.05 : 1.02,
                    }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <IconComponent
                      size={24}
                      className="sm:w-10 sm:h-10 mb-2 sm:mb-4"
                      style={{ color: goal.color }}
                    />
                    <h3
                      className="text-sm sm:text-lg font-semibold mb-1 sm:mb-2"
                      style={{ color: COLORS.charcoal }}
                    >
                      {goal.label}
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-500">
                      {goal.description}
                    </p>
                  </motion.button>
                );
              })}
            </div>

            <div className="flex justify-end">
              <motion.button
                onClick={nextStep}
                disabled={!isStepCompleted(1)}
                className={`px-8 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  isStepCompleted(1)
                    ? 'bg-blue-500 text-white hover:bg-blue-600 shadow-lg'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
                whileHover={isStepCompleted(1) ? { scale: 1.05 } : {}}
                whileTap={isStepCompleted(1) ? { scale: 0.95 } : {}}
              >
                Continue →
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* Step 2: Goal Amount */}
        {currentStep === 2 && (
          <motion.div
            className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-center mb-8">
              <div className="flex items-center justify-center mb-4">
                <selectedGoal.icon
                  size={32}
                  style={{ color: selectedGoal.color }}
                  className="mr-3"
                />
                <h2
                  className="text-3xl font-bold"
                  style={{ color: COLORS.primary }}
                >
                  How much do you need today?
                </h2>
              </div>
              <p className="text-lg text-gray-600">
                Enter the cost of your {selectedGoal.label.toLowerCase()} in
                today's prices
              </p>
            </div>

            <div className="max-w-md mx-auto mb-8">
              <NumberInputWithSlider
                label={`${selectedGoal.label} amount (today's value)`}
                value={goalAmount}
                onChange={setGoalAmount}
                min={10000}
                max={selectedGoalType === 'education' ? 50000000 : 100000000}
                step={10000}
                prefix="₹"
                minLabel={formatIndianNumberShort(10000, false)}
                maxLabel={formatIndianNumberShort(
                  selectedGoalType === 'education' ? 50000000 : 100000000,
                  false
                )}
                showTooltip={true}
              />
            </div>

            <div className="flex justify-between">
              <motion.button
                onClick={prevStep}
                className="px-8 py-3 rounded-xl font-semibold bg-gray-200 text-gray-700 hover:bg-gray-300 transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                ← Back
              </motion.button>
              <motion.button
                onClick={nextStep}
                disabled={!isStepCompleted(2)}
                className={`px-8 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  isStepCompleted(2)
                    ? 'bg-blue-500 text-white hover:bg-blue-600 shadow-lg'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
                whileHover={isStepCompleted(2) ? { scale: 1.05 } : {}}
                whileTap={isStepCompleted(2) ? { scale: 0.95 } : {}}
              >
                Continue →
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* Step 3: Timeline */}
        {currentStep === 3 && (
          <motion.div
            className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-center mb-8">
              <h2
                className="text-3xl font-bold mb-4"
                style={{ color: COLORS.primary }}
              >
                When do you need it?
              </h2>
              <p className="text-lg text-gray-600">
                How many years from now do you want to achieve this goal?
              </p>
            </div>

            <div className="max-w-md mx-auto mb-8">
              <NumberInputWithSlider
                label="Time to achieve goal (years)"
                placeholder="10"
                value={timeToGoal}
                onChange={setTimeToGoal}
                min={1}
                max={50}
                step={1}
                minLabel="1 year"
                maxLabel="50 Years"
              />

              {timeToGoal && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Target year:</strong>{' '}
                    {new Date().getFullYear() + timeToGoal}
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-between">
              <motion.button
                onClick={prevStep}
                className="px-8 py-3 rounded-xl font-semibold bg-gray-200 text-gray-700 hover:bg-gray-300 transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                ← Back
              </motion.button>
              <motion.button
                onClick={nextStep}
                disabled={!isStepCompleted(3)}
                className={`px-8 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  isStepCompleted(3)
                    ? 'bg-blue-500 text-white hover:bg-blue-600 shadow-lg'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
                whileHover={isStepCompleted(3) ? { scale: 1.05 } : {}}
                whileTap={isStepCompleted(3) ? { scale: 0.95 } : {}}
              >
                Continue →
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* Step 4: Inflation Rate */}
        {currentStep === 4 && (
          <motion.div
            className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-center mb-8">
              <h2
                className="text-3xl font-bold mb-4"
                style={{ color: COLORS.primary }}
              >
                Expected inflation rate?
              </h2>
              <p className="text-lg text-gray-600">
                Inflation affects the future cost of your goal. What rate do you
                expect?
              </p>
            </div>

            <div className="max-w-md mx-auto mb-8">
              <NumberInputWithSlider
                label="Expected inflation rate (%)"
                value={inflationRate}
                onChange={setInflationRate}
                min={0.1}
                max={25}
                step={0.5}
                minLabel="0.1%"
                maxLabel="25%"
              />

              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start">
                  <Info
                    size={20}
                    className="text-amber-600 mt-0.5 mr-3 flex-shrink-0"
                  />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium mb-1">Why inflation matters:</p>
                    <p>
                      Due to inflation,{' '}
                      <LocalizedCurrency value={goalAmount || 0} /> today will
                      cost{' '}
                      <LocalizedCurrency
                        value={
                          (goalAmount || 0) *
                          Math.pow(
                            1 + (inflationRate || 6) / 100,
                            timeToGoal || 1
                          )
                        }
                      />{' '}
                      in {timeToGoal || 1} years.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <motion.button
                onClick={prevStep}
                className="px-8 py-3 rounded-xl font-semibold bg-gray-200 text-gray-700 hover:bg-gray-300 transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                ← Back
              </motion.button>
              <motion.button
                onClick={nextStep}
                disabled={!isStepCompleted(4)}
                className={`px-8 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  isStepCompleted(4)
                    ? 'bg-blue-500 text-white hover:bg-blue-600 shadow-lg'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
                whileHover={isStepCompleted(4) ? { scale: 1.05 } : {}}
                whileTap={isStepCompleted(4) ? { scale: 0.95 } : {}}
              >
                Continue →
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* Step 5: Expected Returns */}
        {currentStep === 5 && (
          <motion.div
            className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-center mb-8">
              <h2
                className="text-3xl font-bold mb-4"
                style={{ color: COLORS.primary }}
              >
                Expected annual return rate?
              </h2>
              <p className="text-lg text-gray-600 mb-4">
                What annual return do you expect from your investments?
              </p>
            </div>

            <div className="max-w-md mx-auto mb-8">
              <NumberInputWithSlider
                label="Expected annual return (%)"
                value={expectedReturn}
                onChange={setExpectedReturn}
                min={1}
                max={100}
                step={0.5}
                minLabel="1%"
                maxLabel="100%"
              />

              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                <div
                  className={`p-3 rounded-lg text-center ${expectedReturn && expectedReturn < 8 ? 'bg-red-50 border-red-200 text-red-800' : 'bg-gray-50 border-gray-200 !text-gray-400 opacity-50'}`}
                >
                  <p className="font-medium text-red-800">Conservative</p>
                  <p className="text-red-600">&lt; 8% (Savings/debt funds)</p>
                </div>
                <div
                  className={`p-3 rounded-lg text-center ${expectedReturn && expectedReturn >= 8 && expectedReturn < 12 ? 'bg-yellow-50 border-yellow-200 text-yellow-800' : 'bg-gray-50 border-gray-200 !text-gray-400 opacity-50'}`}
                >
                  <p className="font-medium text-yellow-800">Moderate</p>
                  <p className="text-yellow-600">8-12% (Balanced)</p>
                </div>
                <div
                  className={`p-3 rounded-lg text-center ${expectedReturn && expectedReturn >= 12 ? 'bg-green-50 border-green-200 text-green-800' : 'bg-gray-50 border-gray-200 !text-gray-400 opacity-50'}`}
                >
                  <p className="font-medium text-green-800">Aggressive</p>
                  <p className="text-green-600">12%+ (Equity)</p>
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <motion.button
                onClick={prevStep}
                className="px-8 py-3 rounded-xl font-semibold bg-gray-200 text-gray-700 hover:bg-gray-300 transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                ← Back
              </motion.button>
              <motion.button
                onClick={nextStep}
                disabled={!isStepCompleted(5)}
                className={`px-8 py-3 rounded-xl font-semibold transition-all duration-300 ${
                  isStepCompleted(5)
                    ? 'bg-blue-500 text-white hover:bg-blue-600 shadow-lg'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
                whileHover={isStepCompleted(5) ? { scale: 1.05 } : {}}
                whileTap={isStepCompleted(5) ? { scale: 0.95 } : {}}
              >
                Get your plan →
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* Step 6: Results & Investment Plan */}
        {currentStep === 6 && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-8"
          >
            {/* Summary of Inputs */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <h2
                className="text-2xl font-bold mb-6 text-center"
                style={{ color: COLORS.primary }}
              >
                Your goal planning summary
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                <div className="text-center p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center justify-center mb-2">
                    <selectedGoal.icon
                      size={20}
                      style={{ color: selectedGoal.color }}
                      className="mr-2"
                    />
                    <p className="text-sm text-gray-600">Your goal</p>
                  </div>
                  <p
                    className="text-sm font-bold"
                    style={{ color: selectedGoal.color }}
                  >
                    {selectedGoal.label}
                  </p>
                </div>

                <div className="text-center p-4 bg-gray-50 rounded-xl">
                  <p className="text-sm text-gray-600 mb-2">Goal amount</p>
                  <p
                    className="text-sm font-bold"
                    style={{ color: COLORS.accent }}
                  >
                    <LocalizedCurrency value={goalAmount || 0} />
                  </p>
                </div>

                <div className="text-center p-4 bg-gray-50 rounded-xl">
                  <p className="text-sm text-gray-600 mb-2">Time period</p>
                  <p
                    className="text-sm font-bold"
                    style={{ color: COLORS.primary }}
                  >
                    {timeToGoal} years
                  </p>
                  <p className="text-xs text-gray-500">
                    Target: {new Date().getFullYear() + (timeToGoal || 0)}
                  </p>
                </div>

                <div className="text-center p-4 bg-gray-50 rounded-xl">
                  <p className="text-sm text-gray-600 mb-2">Inflation rate</p>
                  <p
                    className="text-sm font-bold"
                    style={{ color: COLORS.gold }}
                  >
                    {inflationRate}% p.a.
                  </p>
                </div>

                <div className="text-center p-4 bg-gray-50 rounded-xl">
                  <p className="text-sm text-gray-600 mb-2">Expected return</p>
                  <p
                    className="text-sm font-bold"
                    style={{ color: COLORS.sage }}
                  >
                    {expectedReturn || 0}% p.a.
                  </p>
                </div>
              </div>
            </div>

            {/* Inflation Impact */}
            <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-2xl p-8">
              <div className="text-center mb-6">
                <h2
                  className="text-3xl font-bold mb-4"
                  style={{ color: COLORS.primary }}
                >
                  Your goal with inflation impact
                </h2>
                <p className="text-lg text-gray-600">
                  Here's how much you'll actually need in {timeToGoal} years
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="text-center p-6 bg-white rounded-xl shadow-sm">
                  <p className="text-sm text-gray-500 mb-2">Today's cost</p>
                  <p
                    className="text-2xl font-bold"
                    style={{ color: COLORS.accent }}
                  >
                    <LocalizedCurrency value={goalAmount || 0} />
                  </p>
                </div>
                <div className="text-center p-6 bg-white rounded-xl shadow-sm">
                  <p className="text-sm text-gray-500 mb-2">Inflation impact</p>
                  <p
                    className="text-2xl font-bold"
                    style={{ color: COLORS.gold }}
                  >
                    +
                    <LocalizedCurrency
                      value={results.inflationAdjustedGoal - (goalAmount || 0)}
                    />
                  </p>
                </div>
                <div className="text-center p-6 bg-white rounded-xl shadow-sm">
                  <p className="text-sm text-gray-500 mb-2">
                    Future cost ({new Date().getFullYear() + (timeToGoal || 0)})
                  </p>
                  <p
                    className="text-3xl font-bold"
                    style={{ color: COLORS.error }}
                  >
                    <LocalizedCurrency value={results.inflationAdjustedGoal} />
                  </p>
                </div>
              </div>
            </div>

            {/* Investment Solutions */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <h3
                className="text-2xl font-bold mb-6 text-center"
                style={{ color: COLORS.primary }}
              >
                Ways to achieve your goal
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* SIP Option */}
                <div className="p-6 border-2 border-blue-200 rounded-xl bg-blue-50">
                  <div className="flex items-center mb-4">
                    <Calendar
                      size={24}
                      style={{ color: COLORS.accent }}
                      className="mr-3"
                    />
                    <h4
                      className="text-xl font-semibold"
                      style={{ color: COLORS.primary }}
                    >
                      Monthly SIP (recommended)
                    </h4>
                  </div>
                  <div className="mb-4">
                    <p
                      className="text-3xl font-bold"
                      style={{ color: COLORS.accent }}
                    >
                      <LocalizedCurrency value={results.monthlyInvestment} />
                    </p>
                    <p className="text-sm text-gray-600">
                      per month for {timeToGoal} years
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      @ {expectedReturn || 0}% annual return
                    </p>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Total investment:</span>
                      <span className="font-medium">
                        <LocalizedCurrency value={results.totalInvestment} />
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Expected returns:</span>
                      <span className="font-medium text-green-600">
                        <LocalizedCurrency value={results.totalReturns} />
                      </span>
                    </div>
                  </div>
                </div>

                {/* Lumpsum Option */}
                <div className="p-6 border-2 border-green-200 rounded-xl bg-green-50">
                  <div className="flex items-center mb-4">
                    <IndianRupee
                      size={24}
                      style={{ color: COLORS.sage }}
                      className="mr-3"
                    />
                    <h4
                      className="text-xl font-semibold"
                      style={{ color: COLORS.primary }}
                    >
                      One-time investment
                    </h4>
                  </div>
                  <div className="mb-4">
                    <p
                      className="text-3xl font-bold"
                      style={{ color: COLORS.sage }}
                    >
                      <LocalizedCurrency
                        value={
                          results.inflationAdjustedGoal /
                          Math.pow(
                            1 + (expectedReturn || 12) / 100,
                            timeToGoal || 1
                          )
                        }
                      />
                    </p>
                    <p className="text-sm text-gray-600">
                      invest today and let it grow
                    </p>
                    <p className="text-xs text-green-600 mt-1">
                      @ {expectedReturn || 0}% annual return
                    </p>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>One-time investment:</span>
                      <span className="font-medium">
                        <LocalizedCurrency
                          value={
                            results.inflationAdjustedGoal /
                            Math.pow(
                              1 + (expectedReturn || 12) / 100,
                              timeToGoal || 1
                            )
                          }
                        />
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Expected returns:</span>
                      <span className="font-medium text-green-600">
                        <LocalizedCurrency
                          value={
                            results.inflationAdjustedGoal -
                            results.inflationAdjustedGoal /
                              Math.pow(
                                1 + (expectedReturn || 12) / 100,
                                timeToGoal || 1
                              )
                          }
                        />
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Important Disclaimer */}
            <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-6 shadow-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0 mr-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                    <Info size={24} className="text-amber-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-bold text-amber-800 mb-3">
                    Disclaimer
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="font-semibold text-amber-800 mb-2 text-sm">
                        Risk factors:
                      </p>
                      <ul className="space-y-1 text-sm text-amber-700">
                        <li>
                          • <strong>Returns are not guaranteed</strong>
                        </li>
                        <li>• Actual returns may vary significantly</li>
                        <li>• Market volatility can affect outcomes</li>
                        <li>• Past performance ≠ future results</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold text-amber-800 mb-2 text-sm">
                        Professional advice:
                      </p>
                      <ul className="space-y-1 text-sm text-amber-700">
                        <li>
                          •{' '}
                          <strong>Consult a certified financial advisor</strong>
                        </li>
                        <li>• Review your risk tolerance</li>
                        <li>• Diversify your investments</li>
                        <li>• Monitor and rebalance regularly</li>
                      </ul>
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-amber-100 rounded-lg">
                    <p className="text-xs text-amber-800 font-medium">
                      <strong>This calculator provides estimates only.</strong>{' '}
                      Your actual investment journey may differ based on market
                      conditions, underlying instrument performance, and
                      economic factors.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                onClick={prevStep}
                className="px-8 py-3 rounded-xl font-semibold bg-gray-200 text-gray-700 hover:bg-gray-300 transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                ← Modify plan
              </motion.button>
              <motion.button
                onClick={() => {
                  setCurrentStep(1);
                  setSelectedGoalType('');
                }}
                className="px-8 py-3 rounded-xl font-semibold bg-blue-500 text-white hover:bg-blue-600 transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Plan another goal
              </motion.button>
            </div>
          </motion.div>
        )}
      </div>
    </ToolLayout>
  );
};
